import { Injectable } from '@angular/core';
import { QuoteStatus, Quote, Payment } from '../models/quote.model';
import { formatUsd } from '../shared/formatting/usd-currency';

@Injectable({
  providedIn: 'root'
})
export class QuoteUtilsService {
  private static readonly isoDateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;
  private static readonly isoDateTimeWithoutZonePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,7})?)?$/;

  /**
   * Format a number as US currency
   */
  formatCurrency(amount: number): string {
    return formatUsd(amount);
  }

  /**
   * Format a date as a readable string
   */
  formatDate(date: Date | string): string {
    const parsedDate = this.parseDateForDisplay(date);

    if (!parsedDate) {
      return '';
    }

    return parsedDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatDateTime(date: Date | string): string {
    const parsedDate = this.parseDateForDisplay(date);

    if (!parsedDate) {
      return '';
    }

    return parsedDate.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  private parseDateForDisplay(date: Date | string): Date | null {
    if (date instanceof Date) {
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const normalizedDate = date.trim();

    if (!normalizedDate) {
      return null;
    }

    if (QuoteUtilsService.isoDateOnlyPattern.test(normalizedDate)) {
      const [year, month, day] = normalizedDate.split('-').map(Number);
      return new Date(year, month - 1, day);
    }

    if (QuoteUtilsService.isoDateTimeWithoutZonePattern.test(normalizedDate)) {
      return new Date(`${normalizedDate}Z`);
    }

    const parsedDate = new Date(normalizedDate);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  /**
   * Get the Material chip color for a quote status
   */
  getStatusChipColor(status: QuoteStatus): string {
    switch (status) {
      case QuoteStatus.Complete:
        return 'primary';     // Blue - indicates successful completion
      case QuoteStatus.Quoted:
        return 'accent';      // Purple - indicates active/new quotes
      case QuoteStatus.InProgress:
        return 'warn';        // Orange - indicates work in progress
      case QuoteStatus.Delinquent:
        return 'warn';        // Red - indicates problem status
      case QuoteStatus.Grace:
        return 'accent';      // Amber - grace period
      case QuoteStatus.Cancelled:
        return 'default';     // Gray - cancelled
      default:
        return 'default';     // Gray - unknown status
    }
  }

  /**
   * Get the display name for a quote status
   */
  getStatusDisplayName(status: QuoteStatus): string {
    switch (status) {
      case QuoteStatus.Quoted:
        return 'Quoted';
      case QuoteStatus.Complete:
        return 'Complete';
      case QuoteStatus.Delinquent:
        return 'Delinquent';
      case QuoteStatus.InProgress:
        return 'In Progress';
      case QuoteStatus.Grace:
        return 'Grace';
      case QuoteStatus.Cancelled:
        return 'Cancelled';
      default:
        return 'Unknown';
    }
  }

  /**
   * Calculate total premium from quote policies
   */
  getTotalPremium(quote: Quote | null): number {
    if (!quote || !quote.policies || quote.policies.length === 0) {
      return 0;
    }
    return quote.policies.reduce((total, policy) => total + policy.premiumAmount, 0);
  }

  /**
   * Calculate total paid amount from successful payments
   */
  getTotalPaid(quote: Quote | null): number {
    if (!quote || !quote.payments || quote.payments.length === 0) {
      return 0;
    }
    return quote.payments
      .filter(p => p.status.toLowerCase() === 'succeeded')
      .reduce((total, payment) => total + payment.netAmount, 0);
  }

  /**
   * Check if a quote can be viewed (has InProgress, Complete, or Delinquent status)
   */
  canViewQuote(status: QuoteStatus): boolean {
    return status === QuoteStatus.InProgress ||
           status === QuoteStatus.Complete ||
           status === QuoteStatus.Delinquent ||
           status === QuoteStatus.Grace ||
           status === QuoteStatus.Cancelled;
  }

  /**
   * Format quote ID as Q-000001
   */
  formatQuoteId(quoteId: number): string {
    return `Q-${quoteId.toString().padStart(6, '0')}`;
  }
}

